import { env } from "@/env";
import { at } from "./austria";
import { be } from "./belgium";
import { hr } from "./croatia";
import { cz } from "./czechia";
import { dk } from "./denmark";
import { ee } from "./estonia";
import { fi } from "./finland";
import { fr } from "./france";
import { de } from "./germany";
import { gr } from "./greece";
import { hu } from "./hungary";
import { CountryCodeEnum, LanguageCodeEnum, type Market } from "./i18n-config";
import { it } from "./italy";
import { lv } from "./latvia";
import { lt } from "./lithuania";
import { nl } from "./netherlands";
import { no } from "./norway";
import { pl } from "./poland";
import { pt } from "./portugal";
import { ro } from "./romania";
import { sk } from "./slovakia";
import { si } from "./slovenia";
import { es } from "./spain";
import { se } from "./sweden";

export const markets = {
  se,
  no,
  dk,
  fi,
  nl,
  de,
  at,
  be,
  cz,
  ee,
  es,
  fr,
  gr,
  hr,
  hu,
  it,
  lt,
  lv,
  pl,
  pt,
  ro,
  si,
  sk,
};

export function findMarketByLocale(locale: string): Market;

export function findMarketByLocale(locale: string, fallback: true): Market;

export function findMarketByLocale(
  locale: string,
  fallback: false,
): Market | undefined;

export function findMarketByLocale(locale: string, fallback: boolean = true) {
  if (!locale) return getDefaultMarket();

  let market = Object.values(markets).find(
    (market) => market.siteLocale === locale,
  );

  if (!market) {
    market = Object.values(markets).find((market) => market.locale === locale);
  }

  return market ?? (fallback ? markets.de : undefined);
}

export function findMarketByCode(code: string): Market;

export function findMarketByCode(code: string, fallback: true): Market;

export function findMarketByCode(
  code: string,
  fallback: false,
): Market | undefined;

export function findMarketByCode(
  code: string,
  fallback: boolean = true,
): Market | undefined {
  const market = Object.values(markets).find(
    (market) => market.countryCode === code,
  );

  return market ?? (fallback ? markets.de : undefined);
}

export function findMarketByContentfulLocale(locale: string): Market;

export function findMarketByContentfulLocale(
  locale: string,
  fallback: true,
): Market;

export function findMarketByContentfulLocale(
  locale: string,
  fallback: false,
): Market | undefined;

export function findMarketByContentfulLocale(
  locale: string,
  fallback: boolean = true,
) {
  return (
    Object.values(markets).find(
      (market) => market.contentfulLocale === locale,
    ) ?? (fallback ? markets.de : undefined)
  );
}

export function getDefaultMarket(): Market {
  if (env.NEXT_PUBLIC_APP_MARKET === "se") return markets.se;
  if (env.NEXT_PUBLIC_APP_MARKET === "no") return markets.no;
  return markets.de;
}

export function marketCode(market: { code: string } | { countryCode: string }) {
  return "countryCode" in market ? market.countryCode : market.code;
}

export const isValidMarket = (market?: string) => {
  if (!market) return false;
  return Object.values<string>(CountryCodeEnum).includes(market);
};

const NORDIC_CODES = ["se", "no", "dk", "fi"] as const;

const languageNames: Record<LanguageCodeEnum, string> = {
  [LanguageCodeEnum.SE]: "Swedish",
  [LanguageCodeEnum.NO]: "Norwegian",
  [LanguageCodeEnum.EN]: "English",
  [LanguageCodeEnum.FI]: "Finnish",
  [LanguageCodeEnum.DK]: "Danish",
  [LanguageCodeEnum.NL]: "Dutch",
};

export function languageName(code: string) {
  return languageNames[code as LanguageCodeEnum] ?? code;
}

export function marketsByCountryName() {
  return Object.values(markets).sort((a, b) =>
    a.countryName.localeCompare(b.countryName),
  );
}

export function nordicMarkets() {
  return NORDIC_CODES.map((code) => markets[code]);
}

export function europeanMarkets() {
  return Object.values(markets)
    .filter(
      (market) =>
        !NORDIC_CODES.includes(
          market.countryCode as (typeof NORDIC_CODES)[number],
        ),
    )
    .sort((a, b) => a.countryName.localeCompare(b.countryName));
}

export function isValidSiteLocale(locale?: string) {
  const appMarket = env.NEXT_PUBLIC_APP_MARKET;

  if (appMarket === "se" || appMarket === "no") {
    return !locale;
  }

  return Object.values(markets).some(
    (market) =>
      market.countryCode !== "se" &&
      market.countryCode !== "no" &&
      market.siteLocale === locale,
  );
}

export function marketHref(market: Market) {
  return market.siteLocale
    ? `${market.domain}/${market.siteLocale}`
    : market.domain;
}

export function getMarketSwitchHref(from: Market, to: Market) {
  const singleSite = from.countryCode === "se" || from.countryCode === "no";
  const targetSingleSite = to.countryCode === "se" || to.countryCode === "no";

  if (
    from.countryCode === to.countryCode ||
    (!singleSite && !targetSingleSite)
  ) {
    return `${env.NEXT_PUBLIC_APP_BASE_URL}/${to.siteLocale}`;
  }

  return marketHref(to);
}

export function determineSiteName() {
  switch (env.NEXT_PUBLIC_APP_MARKET) {
    case "se":
      return "ngse";
    case "no":
      return "ngno";
    case "de":
      return "ngeu";
    default:
      throw new Error(
        "Unable to determine site name, invalid `env.NEXT_PUBLIC_APP_MARKET`",
      );
  }
}
